import { useTranslation } from 'react-i18next'

import {
  BorderRadius,
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

// PlanTable の cell-changed（前回提出からの変更箇所）と同色。design `.cell-changed`
// 由来のリテラル（#0e7490）。凡例スウォッチと実セルの色を一致させる
// （GenerationSalesPlanDetail `.gs-legend-row` / ProcurementPlanDetail `.sd-legend`
// 両 design 由来で共通）。
// eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (design `.cell-changed` 由来のリテラル、上記コメント参照)
const CHANGED_COLOR = '#0e7490'

/** 凡例に足す項目 (例: 計画内不整合のスウォッチ)。前回差分の項目の後ろに並ぶ。 */
export type PreviousDiffBarExtraItem = {
  key: string
  label: string
  /** スウォッチ (24 x 16) の背景・枠。実セルと同じ定義を渡す */
  swatchStyle: React.CSSProperties
}

type Props = {
  /**
   * 「前回提出からの変更箇所」ラベルの i18n key（呼び出し元ページの namespace 配下）。
   * 省略すると前回差分の項目を出さない (extraItems だけの凡例にする時)。
   */
  changedLabelKey?: string
  /** 前回差分の項目に続けて同じ帯に並べる項目。省略時は従来どおり前回差分の 1 項目だけ */
  extraItems?: readonly PreviousDiffBarExtraItem[]
}

/**
 * 前回提出からの変更箇所の凡例（design `.gs-legend-row` / `.sd-legend` 相当。文言は AC-PPD-019 / AC-PGD-021）。
 * caller は、前回提出データがあって前回差分の項目を出す時、または `extraItems` がある時に描画する。
 * GenerationSalesPlanDetail / ProcurementPlanDetail 共通コンポーネント
 * （旧実装は i18n key 以外まったく同一のコピペ 2 ファイルだった）。
 *
 * 既定では「前回提出からの変更箇所」1 項目。画面が `extraItems` を渡した時だけ、同じ帯に
 * 続けて項目を並べる (需要調達計画詳細の計画内不整合 AC-PPD-024 / 025)。取引相手間ミスマッチの
 * 凡例項目は未対応。
 */
export const PreviousDiffBar = ({
  changedLabelKey,
  extraItems = [],
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        padding: '6px 10px',
        fontSize: fontSizes.sm.value,
        // raw DOM の style object のため LightModeSemanticColors の .value (生 hex) を使う
        color: LightModeSemanticColors.Text.midEmphasis.value,
      }}
    >
      {changedLabelKey !== undefined && (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          {/* スウォッチ: 変化セルの見え方（teal 太字の数値）を縮約表現 */}
          <span
            style={{
              display: 'inline-block',
              minWidth: '32px',
              padding: '1px 4px',
              background: LightModeSemanticColors.Surface.primary.value,
              border: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
              // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
              borderRadius: '2px',
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <b style={{ color: CHANGED_COLOR, fontSize: '11px' }}>123</b>
          </span>
          {t(changedLabelKey)}
        </span>
      )}
      {extraItems.map((item) => (
        <span
          key={item.key}
          data-testid={`plan-legend-item-${item.key}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: '24px',
              height: '16px',
              borderRadius: BorderRadius.radiiSm.value,
              ...item.swatchStyle,
            }}
          />
          {item.label}
        </span>
      ))}
    </div>
  )
}
